import React from 'react';
import { Album } from '../../types/album';
import { theme } from '../../styles/theme';
import { 
  generateVisualizationData, 
  ShelfVisualizationData,
  ShelfConfig,
  getAlbumsInShelf
} from '../../utils/shelfCalculation';

interface ShelfLocationProps {
  album: Album;
  allAlbums: Album[];
  shelfConfig: ShelfConfig;
  onShowSettings: () => void;
}

const ShelfLocation: React.FC<ShelfLocationProps> = ({ album, allAlbums, shelfConfig, onShowSettings }) => {
  const visualData = generateVisualizationData(album, allAlbums, shelfConfig);
  
  // Calculate container dimensions - different heights for different layouts
  const containerWidth = 300;
  const containerPadding = 32; // 16px on each side
  const titleHeight = 60; // Space for title and margins (more realistic)
  const disclaimerHeight = 25; // Space for disclaimer at bottom
  const arrowSpacing = 25; // Space for arrow in vertical layouts
  const bufferSpace = 10; // Safety buffer
  
  // Determine layout and calculate required space
  const { rows, columns, shelfDimensions } = shelfConfig;
  const gridAspectRatio = (columns * shelfDimensions.widthRatio) / (rows * shelfDimensions.heightRatio);
  const shelfAspectRatio = shelfDimensions.widthRatio / shelfDimensions.heightRatio;
  
  const availableWidth = containerWidth - containerPadding;
  
  // Calculate space needed for horizontal layout - shelf should be bigger than grid
  const horizontalGridWidth = availableWidth * 0.3; // Give grid less space  
  const horizontalShelfWidth = availableWidth * 0.65; // Give shelf more space - it should be the main focus
  const horizontalGridHeight = horizontalGridWidth / gridAspectRatio;
  const horizontalShelfHeight = horizontalShelfWidth / shelfAspectRatio;
  const horizontalMaxHeight = Math.max(horizontalGridHeight, horizontalShelfHeight);
  const horizontalTotalHeight = horizontalMaxHeight + titleHeight + disclaimerHeight + containerPadding + bufferSpace;
  
  // Calculate space needed for vertical layout
  const verticalWidth = availableWidth * 0.85;
  const verticalGridHeight = verticalWidth / gridAspectRatio;
  const verticalShelfHeight = verticalWidth / shelfAspectRatio;
  const verticalContentHeight = verticalGridHeight + verticalShelfHeight + arrowSpacing;
  const verticalTotalHeight = verticalContentHeight + titleHeight + disclaimerHeight + containerPadding + bufferSpace;
  
  // Layout decision: use vertical stacking when components are wide
  // For 1×2 grid with 3:1 shelves: gridAspectRatio = 6, shelfAspectRatio = 3
  // Wide components should be stacked vertically, not placed side-by-side
  const useVerticalLayout = gridAspectRatio > 2.5 || shelfAspectRatio > 2.5;
  const containerHeight = useVerticalLayout ? Math.max(400, Math.min(verticalTotalHeight, 550)) : Math.min(horizontalTotalHeight, 300);
  
  // Calculate actual available height for content
  const availableHeight = containerHeight - titleHeight - disclaimerHeight - containerPadding - bufferSpace;
  
  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surfaceLight,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    marginTop: theme.spacing.md,
    cursor: 'pointer',
    transition: `all ${theme.transitions.fast}`,
    border: `1px solid ${theme.colors.border}`,
    height: `${containerHeight}px`, // Fixed height
    boxSizing: 'border-box',
    overflow: 'hidden',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1rem',
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
    textAlign: 'center',
  };

  const mainLayoutStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: useVerticalLayout ? 'column' : 'row',
    alignItems: 'center',
    gap: theme.spacing.lg,
    marginBottom: theme.spacing.md,
  };

  const overviewContainerStyle: React.CSSProperties = {
    flex: '0 0 auto',
  };

  const detailContainerStyle: React.CSSProperties = {
    flex: '1',
    minWidth: 0,
  };

  const arrowStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    color: theme.colors.primary,
    flex: '0 0 auto',
    transform: useVerticalLayout ? 'rotate(90deg)' : 'translate(4px, 8px)', // Vertical arrow for stacked layout
  };

  const disclaimerStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    color: theme.colors.textMuted,
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: theme.spacing.sm,
  };

  return (
    <div 
      style={containerStyle}
      onClick={onShowSettings}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.colors.surface;
        e.currentTarget.style.borderColor = theme.colors.primary;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = theme.colors.surfaceLight;
        e.currentTarget.style.borderColor = theme.colors.border;
      }}
    >
      <div style={titleStyle}>Shelf Location</div>
      
      {visualData.currentShelf.shelfNumber > 0 ? (
        <>
          <div style={mainLayoutStyle}>
            <div style={overviewContainerStyle}>
              <ShelfOverview 
                visualData={visualData} 
                useVerticalLayout={useVerticalLayout} 
                allocatedWidth={useVerticalLayout ? verticalWidth : horizontalGridWidth}
                allocatedHeight={useVerticalLayout ? 
                  availableHeight * 0.5 : // Give grid 50% in vertical
                  availableHeight // Full height in horizontal
                }
              />
            </div>
            
            <div style={arrowStyle}>→</div>
            
            <div style={detailContainerStyle}>
              <ShelfDetail 
                visualData={visualData} 
                album={album} 
                allAlbums={allAlbums} 
                shelfNumber={visualData.currentShelf.shelfNumber}
                shelfConfig={shelfConfig}
                useVerticalLayout={useVerticalLayout}
                allocatedWidth={useVerticalLayout ? verticalWidth : horizontalShelfWidth}
                allocatedHeight={useVerticalLayout ? 
                  availableHeight * 0.45 : // Give shelf reasonable space
                  availableHeight // Full height in horizontal
                }
              />
            </div>
          </div>
          
          <div style={disclaimerStyle}>
            Position approximated • Click to configure shelves
          </div>
        </>
      ) : (
        <>
          <div style={{
            color: theme.colors.textMuted,
            fontSize: '0.875rem',
            marginBottom: theme.spacing.sm,
            textAlign: 'center'
          }}>
            Position not calculated
          </div>
          <div style={disclaimerStyle}>
            Configure shelf sections in settings
          </div>
        </>
      )}
    </div>
  );
};

// Generic Shelf Overview Component
const ShelfOverview: React.FC<{ visualData: ShelfVisualizationData; useVerticalLayout: boolean; allocatedWidth: number; allocatedHeight: number }> = ({ visualData, useVerticalLayout, allocatedWidth, allocatedHeight }) => {
  const { overview } = visualData;
  
  // Use the exact allocated dimensions - scale to fit within them
  const aspectRatio = overview.shelfDimensions.widthRatio / overview.shelfDimensions.heightRatio;
  const totalAspectRatio = (overview.columns * aspectRatio) / overview.rows;
  
  // Scale to fit within allocated space while maintaining aspect ratio
  let gridWidth = allocatedWidth;
  let gridHeight = gridWidth / totalAspectRatio;
  
  // If calculated height exceeds allocated height, scale down
  if (gridHeight > allocatedHeight) {
    gridHeight = allocatedHeight;
    gridWidth = gridHeight * totalAspectRatio;
  }
  
  // Ensure we don't exceed allocated dimensions
  gridWidth = Math.min(gridWidth, allocatedWidth);
  gridHeight = Math.min(gridHeight, allocatedHeight);
  
  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateRows: `repeat(${overview.rows}, 1fr)`,
    gridTemplateColumns: `repeat(${overview.columns}, 1fr)`,
    gap: '2px',
    width: `${gridWidth}px`,
    height: `${gridHeight}px`,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.sm,
    padding: '5px',
  };

  const shelfStyle = (shelfNumber: number): React.CSSProperties => ({
    backgroundColor: shelfNumber === overview.currentShelf ? '#ef4444' : 
                    shelfNumber > 0 ? theme.colors.border : theme.colors.surface,
    borderRadius: '2px',
    transition: `background-color ${theme.transitions.fast}`,
  });

  return (
    <div>
      <div style={{
        fontSize: '0.75rem',
        color: theme.colors.textSecondary,
        marginBottom: '4px',
        textAlign: 'center'
      }}>
        {overview.rows}×{overview.columns} Grid<br/>
        ({overview.shelfDimensions.heightRatio.toFixed(1)}H × {overview.shelfDimensions.widthRatio.toFixed(1)}W)
      </div>
      <div style={gridStyle}>
        {overview.shelfLayout.flat().map((shelfNumber, index) => (
          <div key={index} style={shelfStyle(shelfNumber)} />
        ))}
      </div>
    </div>
  );
};

// Detailed Shelf View Component
const ShelfDetail: React.FC<{ visualData: ShelfVisualizationData; album: Album; allAlbums: Album[]; shelfNumber: number; shelfConfig: ShelfConfig; useVerticalLayout: boolean; allocatedWidth: number; allocatedHeight: number }> = ({ 
  visualData, album, allAlbums, shelfNumber, shelfConfig, useVerticalLayout, allocatedWidth, allocatedHeight 
}) => {
  const { currentShelf } = visualData;
  
  const shelfContainerStyle: React.CSSProperties = {
    marginBottom: theme.spacing.sm,
  };

  const shelfTitleStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: '6px',
    textAlign: 'center',
  };

  // Use the exact allocated dimensions - scale to fit within them
  const { heightRatio, widthRatio } = shelfConfig.shelfDimensions;
  const aspectRatio = widthRatio / heightRatio;
  
  // Ensure we fit within allocated bounds - calculate maximum possible dimensions
  const maxShelfHeight = allocatedHeight - 50; // Leave room for title and scale (50px total)
  
  // Scale to fit within allocated space while maintaining aspect ratio
  let shelfWidth = allocatedWidth;
  let shelfHeight = shelfWidth / aspectRatio;
  
  // If calculated height exceeds maximum allowed height, scale down
  if (shelfHeight > maxShelfHeight) {
    shelfHeight = maxShelfHeight;
    shelfWidth = shelfHeight * aspectRatio;
  }
  
  // Final bounds check - ensure we don't exceed allocated dimensions
  shelfWidth = Math.min(shelfWidth, allocatedWidth);
  shelfHeight = Math.min(shelfHeight, maxShelfHeight);
  
  // Ensure minimum size for visibility
  const minShelfHeight = 60;
  const minShelfWidth = 120;
  if (shelfHeight < minShelfHeight) {
    shelfHeight = minShelfHeight;
    shelfWidth = shelfHeight * aspectRatio;
  }
  if (shelfWidth < minShelfWidth) {
    shelfWidth = minShelfWidth;
    shelfHeight = shelfWidth / aspectRatio;
    // Re-check height constraint
    if (shelfHeight > maxShelfHeight) {
      shelfHeight = maxShelfHeight;
      shelfWidth = shelfHeight * aspectRatio;
    }
  }

  const shelfVisualizationStyle: React.CSSProperties = {
    height: `${shelfHeight}px`,
    width: `${shelfWidth}px`,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.sm,
    border: `2px solid ${theme.colors.border}`,
    position: 'relative',
    overflow: 'hidden',
    marginBottom: '12px',
    margin: '0 auto',
  };

  const recordDivisionsStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
  };

  const scaleStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
    color: theme.colors.textMuted,
    position: 'relative',
    width: `${shelfWidth}px`, // Match the dynamic visualization width
    margin: '0 auto',
  };

  const scaleLineStyle: React.CSSProperties = {
    position: 'absolute',
    top: '-6px',
    left: 0,
    right: 0,
    height: '1px',
    backgroundColor: theme.colors.border,
  };

  // Get albums in this shelf using the proper shelf calculation utility
  const getShelfAlbums = () => {
    return getAlbumsInShelf(shelfNumber, allAlbums, shelfConfig);
  };

  const shelfAlbums = getShelfAlbums();
  const currentAlbumIndex = shelfAlbums.findIndex(a => a.id === album.id);
  
  // Get the proper shelf capacity from shelf calculation
  const { totalAlbums: shelfCapacity } = visualData.currentShelf;
  
  // Calculate cumulative physical sizes for proportional display
  const albumSizes = shelfAlbums.map(a => a.format?.physicalSize || 1.0);
  const totalPhysicalSize = albumSizes.reduce((sum, size) => sum + size, 0);
  
  // For visual width calculation, use the capacity as the base
  const averageAlbumSize = totalPhysicalSize / shelfAlbums.length;
  const capacityPhysicalSize = shelfCapacity * averageAlbumSize;
  
  // Create segments based on actual physical sizes, but relative to shelf capacity
  const segments = albumSizes.map((size, index) => ({
    size,
    widthPercent: (size / capacityPhysicalSize) * 100,
    isCurrentAlbum: index === currentAlbumIndex,
    album: shelfAlbums[index]
  }));

  const currentPosition = currentAlbumIndex + 1;
  const totalAlbums = shelfCapacity;

  return (
    <div style={shelfContainerStyle}>
      <div style={shelfTitleStyle}>
        Shelf {currentShelf.shelfNumber} • Position {currentPosition} of {totalAlbums}
      </div>
      
      <div style={shelfVisualizationStyle}>
        <div style={recordDivisionsStyle}>
          {segments.map((segment, i) => (
            <div
              key={i}
              style={{
                width: `${segment.widthPercent}%`,
                backgroundColor: segment.isCurrentAlbum ? '#ef4444' : 'transparent',
                borderRight: i < segments.length - 1 ? `0.5px solid ${theme.colors.border}` : 'none',
                opacity: segment.isCurrentAlbum ? 0.8 : 0.3,
                position: 'relative',
              }}
              title={`${segment.album.artist} - ${segment.album.title} (${segment.size.toFixed(1)}×)`}
            />
          ))}
        </div>
      </div>
      
      <div style={scaleStyle}>
        <div style={scaleLineStyle} />
        <span>1</span>
        <span>{Math.floor(totalAlbums / 2)}</span>
        <span>{totalAlbums}</span>
      </div>
    </div>
  );
};

export default ShelfLocation;